import { CSSProperties } from 'react';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { Styles } from '@vivli/shared/theme';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { ICellRendererParams } from 'ag-grid-community';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';
import { useAssignedAppTypeContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { useTranslation } from 'react-i18next';
import { TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { CancelButtonView } from '../shared/cancel-button.component';
import { DTIDataRequestStatusRenderer } from '@vivli/shared/infrastructure/constants';

const containerStyle: CSSProperties = {
    color: 'rgb(176, 176, 176)',
    fontSize: '15px',
    display: 'flex',
    minWidth: '440px',
    alignItems: 'center',
    height: '100%'
};

const titleStyle: CSSProperties = {
    color: 'rgb(0, 0, 0)',
    textAlign: 'center',
    flex: 1
};

const bodyContainerStyle: CSSProperties = {
    fontSize: '15px',
    display: 'flex',
    justifyContent: 'flex-end',
    flex: 1
};

const bodyStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center'
};

const removeBtnStyle: CSSProperties = {
    ...Styles.Button.BUTTON_RED_X,
    marginRight: 50,
    justifyContent: 'flex-start',
    paddingLeft: '12px',
    paddingBottom: '3px'
};

interface DataRequestStatusRendererComponentProps extends ICellRendererParams {
    data: IDataRequestSummary;
    hideStatus?: boolean;
    alignRight?: boolean;
    showRemoveButton?: () => void;
}

export const DataRequestStatusRenderer = ({
    data,
    hideStatus,
    alignRight,
    showRemoveButton,
    node,
    api
}: DataRequestStatusRendererComponentProps) => {
    const toast = useToastApiContext();
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const { isAmr } = useAssignedAppTypeContext();
    const statusMessage = DataRequestFormatService.mapStatusToMessage(DataRequestStatusEnum[data.status], isAmr, duaTranslation);

    const handleRemoveRow = () => {
        toast.success(`${data.requestTitle} successfully withdrawn.`);
        // DEV-1003: a paged (infinite) grid rejects transactions; reload its pages, which also refreshes the tab count
        if (api.getGridOption('rowModelType') === 'infinite') {
            api.refreshInfiniteCache();
        } else {
            api.applyTransaction({ remove: [data] });
        }
    };

    return (
        <div style={containerStyle} data-test-id={DTIDataRequestStatusRenderer.DataRequestStatus + data.requestTitle}>
            {!hideStatus && (
                <div
                    style={{
                        ...titleStyle,
                        flex: showRemoveButton && data.status == DataRequestStatusEnum.Draft ? 1 : 'auto',
                        marginLeft: 'auto'
                    }}
                >
                    {statusMessage}
                </div>
            )}
            <div style={bodyContainerStyle}>
                {showRemoveButton && data.status == DataRequestStatusEnum.Draft && (
                    <CancelButtonView dataRequest={data} style={removeBtnStyle} updateDataRequest={handleRemoveRow} />
                )}
            </div>
        </div>
    );
};
